import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { IconCredit, LegalLinks, Masthead } from '../components/Layout.tsx';
import { Icon } from '../components/Icon.tsx';
import { termsDate } from '../components/Terms.tsx';
import { PageTitle } from '../components/ui.tsx';
import { UI } from '../icons/ui-icons.ts';
import { OPERATOR, STEWARDS_EMAIL } from '../lib/stewards.ts';

/** The report button's icon, so the rules show what to look for. */
const Flag = () => <Icon name={UI.report} className="text-ink-dim" />;
const Mail = () => <a className="text-gilt underline" href={`mailto:${STEWARDS_EMAIL}`}>{STEWARDS_EMAIL}</a>;
const Out = ({ href, children }: { href: string; children: ReactNode }) => (
  <a className="text-gilt underline" href={href} target="_blank" rel="noreferrer">{children}</a>
);

/** The rules, the terms and the privacy notice: readable signed out, linked from every footer. */
function LegalPage({ icon, title, children }: { icon: string; title: string; children: ReactNode }) {
  return (
    <div className="mx-auto flex min-h-dvh max-w-2xl flex-col px-4">
      <header className="border-b border-rule py-3"><Masthead /></header>
      <main className="flex-1 space-y-6 py-8 leading-relaxed [&_li]:ml-5 [&_li]:list-disc [&_section]:space-y-2 [&_ul]:space-y-1">
        <PageTitle icon={icon}>{title}</PageTitle>
        <p className="text-sm text-ink-faint">Last changed {termsDate}.</p>
        {children}
      </main>
      <footer className="flex flex-wrap gap-3 border-t border-rule py-3 text-xs text-ink-faint">
        <LegalLinks />
        <IconCredit />
      </footer>
    </div>
  );
}

export function Rules() {
  return (
    <LegalPage icon={UI.rules} title="The rules">
      <p>
        Wynchester is a rough place. Fights, robbery and crime are part of the game, and you can be as ruthless as you like with
        someone’s <em>character</em>. Every character is played by a real person, though, and that person must be treated decently.
      </p>
      <section>
        <h2 className="heading">Not allowed</h2>
        <p>These rules cover chat, character names and trade offers.</p>
        <ul>
          <li><strong>Hate.</strong> Attacking or mocking anyone for their race, religion, sex, gender, sexuality, disability or where they come from.</li>
          <li><strong>Harassment.</strong> Picking on someone again and again, following them about to taunt them, or getting others to gang up on them. Beating someone in a fight is fine. Hunting the same person down day after day to upset them is not.</li>
          <li><strong>Sexual content.</strong> No sexual talk of any kind. Never ask anyone for pictures, or for their age, where they live, or to talk somewhere private.</li>
          <li><strong>Threats.</strong> Threatening to hurt anyone in real life, even as a joke, or telling anyone to hurt themselves.</li>
          <li><strong>Scams.</strong> Lying to get someone’s coin or goods, pretending to be a steward, or asking for anyone’s password or sign-in code. Robbing someone after a fight is part of the game. Tricking them is not.</li>
          <li><strong>Real-money trading.</strong> Buying or selling coin, goods, characters or accounts for real money, or for anything outside the game.</li>
          <li><strong>Offensive names.</strong> Names that are rude, hateful or sexual, or that pretend to be a steward or a real person.</li>
          <li><strong>Personal details.</strong> Sharing anyone’s real name, address, school, phone number or social media, including your own.</li>
          <li><strong>Spam.</strong> Flooding chat with the same thing, or with adverts.</li>
        </ul>
      </section>
      <section>
        <h2 className="heading">What the stewards can do</h2>
        <p>Stewards are the people who look after Wynchester. If you break the rules, a steward can:</p>
        <ul>
          <li>remove what you said,</li>
          <li>silence you, so you can play but not chat,</li>
          <li>banish you from the game for a time or for good,</li>
          <li>make you choose a new name.</li>
        </ul>
        <p>You will be told why. If you think a steward got it wrong, you can appeal: see <Link to="/terms" className="text-gilt underline">the terms</Link>.</p>
      </section>
      <section>
        <h2 className="heading">If someone breaks the rules</h2>
        <p>
          Press the flag <Flag /> next to what they said in Talk, or next to their name in Folk about town, a fight or a trade offer. Pick what was
          wrong, add a note if you like, and a steward will look into it. You can also write to <Mail /> with their character’s name, what happened
          and roughly when. You don’t have to argue with them, and it’s best not to.
        </p>
        <p>
          You can also block them from your <Link to="/character" className="text-gilt underline">character sheet</Link>. You won’t see what they say,
          they drop out of your Folk about town, and they can’t send you trade offers. They are not told. If you’d rather not see chat at all, you can
          turn it off there too.
        </p>
      </section>
      <section>
        <h2 className="heading">If you feel unsafe</h2>
        <ul>
          <li>Stop talking to them, block them, and tell an adult you trust.</li>
          <li>If someone is in danger right now, call 999.</li>
          <li>If someone online is making you worried or uncomfortable, especially an adult, you can report it to <Out href="https://www.ceop.police.uk/Safety-Centre/">CEOP</Out>, part of the police.</li>
          <li>You can talk to <Out href="https://www.childline.org.uk/">Childline</Out> on 0800 1111 about anything, for free.</li>
        </ul>
      </section>
    </LegalPage>
  );
}

export function Terms() {
  return (
    <LegalPage icon={UI.terms} title="The terms">
      <p>
        These are the terms for playing Wynchester. “We” means {OPERATOR}, who makes and runs the game in the United Kingdom. “You” means
        you, the player. By making a character you agree to them.
      </p>
      <section>
        <h2 className="heading">Who can play</h2>
        <ul>
          <li>You must be <strong>13 or over</strong>.</li>
          <li>If you are under 18, tell a parent or guardian that you play.</li>
          <li>One character per account. Keep your sign-in to yourself: you are responsible for what is done with your account.</li>
        </ul>
      </section>
      <section>
        <h2 className="heading">Playing fair</h2>
        <p>You agree to follow <Link to="/rules" className="text-gilt underline">the rules</Link>. They say what isn’t allowed and what the stewards can do about it.</p>
      </section>
      <section>
        <h2 className="heading">The game</h2>
        <ul>
          <li>Wynchester is free to play.</li>
          <li>Coin and goods in the game are not worth real money. You can’t buy them or cash them out.</li>
          <li>The game is still being made. We may change how things work, and sometimes things will go wrong. We can’t promise it will always be there or that nothing will be lost.</li>
        </ul>
      </section>
      <section>
        <h2 className="heading">Reporting</h2>
        <p>
          If someone breaks the rules, press the flag <Flag /> next to what they said or next to their name, or write to <Mail /> with their
          character’s name, what happened and roughly when. A steward will look into it, and we aim to do so within 48 hours. We won’t tell the
          other player who reported them, or tell you what was done about it.
        </p>
      </section>
      <section>
        <h2 className="heading">Appeals and complaints</h2>
        <p>
          If you were silenced, banished or made to change your name and think it was wrong, write to <Mail /> with your character’s name
          and why. We will look at it again and write back. Write to the same address with any other complaint, including about how we
          keep players safe.
        </p>
      </section>
      <section>
        <h2 className="heading">Leaving</h2>
        <p>
          You can stop playing at any time. To have your account deleted, write to <Mail /> from the email address you sign in with.
          We may close an account for serious or repeated rule-breaking.
        </p>
      </section>
      <section>
        <h2 className="heading">Changes</h2>
        <p>If we change these terms, the rules or the <Link to="/privacy" className="text-gilt underline">privacy notice</Link>, we will ask you to agree again the next time you play.</p>
      </section>
      <p className="text-sm text-ink-dim">These terms are under the law of England and Wales.</p>
    </LegalPage>
  );
}

const KEPT: [what: string, why: string, howLong: string][] = [
  ['Your email address, and your passkey if you made one', 'To sign you in', 'While you have an account'],
  ['Your character and everything it owns and has done', 'To play the game', 'While you have an account'],
  ['The month and year you were born', 'To check you are 13 or over, and to keep analytics off by default if you are under 18', 'While you have an account'],
  ['Play analytics: what your character does (never what you say), sent to PostHog under your player id', 'To see which parts of the game work and which to improve', 'Up to 1 year (PostHog’s limit on our plan), or until you ask us to delete it'],
  ['Site analytics, only if you say yes: pages visited and buttons clicked, your browser and rough location from your IP address', 'The same', 'Up to 1 year (PostHog’s limit on our plan), or until you ask us to delete it'],
  ['Your Chronicle (the list of what happened to your character)', 'To show you what happened', '90 days'],
  ['Chat', 'So people can read it', '30 days, then deleted'],
  ['Who you have blocked, and whether your chat is off', 'To keep their words and offers away from you', 'Until you unblock them or turn chat back on'],
  ['Reports, with a copy of the reported line and the 5 lines either side', 'So a steward can judge it fairly', 'Until a steward deals with it, then 90 days'],
  ['The moderation log: what a steward did to an account, and why', 'For appeals and evidence', '1 year'],
  ['A scrambled form of your IP address and a random id your browser keeps', 'To spot one person trading with themselves on two accounts', 'With each trade offer until it closes; 180 days if a trade is flagged'],
  ['If email to you bounces or you mark it as spam, a scrambled form of your address', 'So we stop sending to it', 'Until you ask us to remove it'],
  ['Server logs', 'To find and fix faults', '2 weeks'],
  ['A daily copy of all of the above but your email address and passkey', 'To check the game stays fair and balanced', '30 days, so anything above can last up to 30 days longer in a copy'],
];

export function Privacy() {
  return (
    <LegalPage icon={UI.privacy} title="Privacy">
      <p>
        This says what Wynchester keeps about you, why, and for how long. {OPERATOR} runs Wynchester and is responsible for your data. Ask
        anything at <Mail />.
      </p>
      <section>
        <h2 className="heading">What we keep</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-ink-dim">
              <tr className="border-b border-rule"><th className="py-1 pr-3 font-normal">What</th><th className="py-1 pr-3 font-normal">Why</th><th className="py-1 font-normal">How long</th></tr>
            </thead>
            <tbody>
              {KEPT.map(([what, why, howLong]) => (
                <tr key={what} className="border-b border-rule/50 align-top"><td className="py-1.5 pr-3">{what}</td><td className="py-1.5 pr-3 text-ink-dim">{why}</td><td className="py-1.5">{howLong}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>“Scrambled” means hashed: we can tell when two match, but not turn them back into the address or id.</p>
      </section>
      <section>
        <h2 className="heading">Who can see it</h2>
        <ul>
          <li>Other players see your character’s name, level and town, what you say in chat, and your fights and trade offers with them.</li>
          <li>If you are among the top 50 on a leaderboard, everyone sees your name, Calling and score there: your Renown, the coin you hold, or a skill.</li>
          <li>Stewards can see reports, the moderation log and recent chat, to keep players safe.</li>
          <li>Everything is stored with Amazon Web Services in London, apart from analytics, which PostHog keeps for us in the EU.</li>
          <li>Emails to you, such as your sign-up code, are sent for us by Resend, which sees your address and the email to deliver it and keeps a short record of what it sent.</li>
          <li>We never sell your data, show you adverts or track you on other sites.</li>
        </ul>
      </section>
      <section>
        <h2 className="heading">In your browser</h2>
        <p>Your browser keeps your sign-in and the random id above, in its local storage, and your answer to the analytics question.</p>
        <p>
          If you say yes to analytics, PostHog’s script is loaded and sets its own cookie, so it can tell your visits apart. If you say no,
          or don’t answer, it is never loaded. You can change your answer at any time with “Analytics choice” at the foot of every page.
          We never record your screen, and we use no advertising cookies.
        </p>
      </section>
      <section>
        <h2 className="heading">Why we may use it</h2>
        <ul>
          <li>To run the game you asked to play.</li>
          <li>To keep players safe and stop cheating, which players and we both have a real interest in.</li>
          <li>
            Play analytics, to make the game better, which we have a real interest in. They are on for players 18 and over unless they turn
            them off, and off for players under 18 unless they turn them on, on the Character page. Site analytics only with your yes.
          </li>
          <li>Because the law, including the Online Safety Act, asks us to deal with harmful content.</li>
        </ul>
      </section>
      <section>
        <h2 className="heading">Your rights</h2>
        <p>
          You can ask for a copy of what we hold about you, ask us to correct it, or ask us to delete your account. Write to <Mail /> from
          the email address you sign in with. If you are unhappy with how we handle your data, you can complain to
          the <Out href="https://ico.org.uk/make-a-complaint/">Information Commissioner’s Office</Out>.
        </p>
      </section>
    </LegalPage>
  );
}
